import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * VFButton — bouton primaire du design system VF.
 */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'success' | 'danger' | 'outline';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:   'bg-[var(--vf-primary)] text-white hover:bg-[var(--vf-primary-hover)] active:bg-[var(--vf-primary-hover)]',
  secondary: 'bg-[var(--vf-surface-2)] text-[var(--vf-text-1)] border border-[var(--vf-border)] hover:bg-[var(--vf-surface-3)] hover:border-[var(--vf-border-hi)]',
  ghost:     'text-[var(--vf-text-2)] hover:bg-[var(--vf-surface-2)] hover:text-[var(--vf-text-1)]',
  success:   'bg-[var(--vf-green)] text-white hover:brightness-110',
  danger:    'bg-[var(--vf-red)] text-white hover:brightness-110',
  outline:   'border border-[var(--vf-border-hi)] text-[var(--vf-text-1)] hover:bg-[var(--vf-surface-2)]',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-xs gap-1',
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-[13px] gap-1.5',
  lg: 'h-11 px-5 text-sm gap-2',
};

interface VFButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
}

export function VFButton({
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  children,
  loading = false,
  disabled,
  className = '',
  type = 'button',
  ...rest
}: VFButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={[
        'inline-flex items-center justify-center rounded-vf font-medium',
        'transition-all duration-150 select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--vf-bg)]',
        'disabled:opacity-50 disabled:pointer-events-none',
        VARIANTS[variant] || VARIANTS.secondary,
        SIZES[size] || SIZES.md,
        className,
      ].join(' ')}
    >
      {loading ? (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />
      ) : icon}
      {children && <span>{children}</span>}
      {iconRight}
    </button>
  );
}

interface VFIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg';
  title?: string;
}

export function VFIconButton({ size = 'md', className = '', title, children, ...rest }: VFIconButtonProps) {
  const s = size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-10 w-10' : 'h-8 w-8';
  return (
    <button
      {...rest}
      title={title}
      aria-label={title}
      className={[
        'inline-flex items-center justify-center rounded-vf-sm',
        'text-[var(--vf-text-2)] hover:text-[var(--vf-text-1)] hover:bg-[var(--vf-surface-2)]',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vf-accent)]',
        s,
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export default VFButton;
